import { UAParser } from 'ua-parser-js';
import { type PlatformInfo, parseDeviceType } from './userAgent';
import { osDetector } from './osDetector';

const userAgentParser = new UAParser();

export interface PlatformInfoClientHints extends PlatformInfo {
    cpuArchitecture?: string;
}

let cache: PlatformInfoClientHints | undefined = undefined;

export const getPlatformInfoClientHints = async (): Promise<PlatformInfoClientHints> => {
    if (cache) {
        return cache;
    }

    cache = await getPlatformInfoClientHintsInternal();

    return cache;
};

const getPlatformInfoClientHintsInternal = async (): Promise<PlatformInfoClientHints> => {
    // `ua-parser-js` package uses Client Hints (NavigatorUAData API) to collect precise OS version, CPU architecture and others
    // It's currently supported only in Chrome and Edge.
    // See more: https://developer.mozilla.org/en-US/docs/Web/API/User-Agent_Client_Hints_API
    if (!isUserAgentClientHintsSupported()) {
        return {};
    }

    const [browser, engine, os, device, cpu] = await Promise.all([
        userAgentParser.getBrowser().withClientHints(),
        userAgentParser.getEngine().withClientHints(),
        osDetector(),
        userAgentParser.getDevice().withClientHints(),
        userAgentParser.getCPU().withClientHints(),
    ]);

    return {
        browserName: browser.name,
        browserVersion: browser.version,
        engineName: engine.name,
        engineVersion: engine.version,
        osName: os.name,
        osVersion: os.version,
        deviceType: parseDeviceType(device.type),
        deviceModel: device.model,
        deviceVendor: device.vendor,
        cpuArchitecture: cpu.architecture,
    };
};

export const isUserAgentClientHintsSupported = (): boolean => {
    return !!navigator.userAgentData;
};

export const UNSUPPORTED = 'unsupported';
